import React, { useCallback, useEffect, useMemo, useState } from "react";
import "@/App.css";
import { Toaster, toast } from "sonner";
import {
  Upload, Settings as SettingsIcon, Play, Download, RefreshCw, Trash2,
  Search, Building2, Users, Cpu, CheckCircle2, AlertTriangle, ChevronDown,
  ChevronRight, FileText, Zap, Mail, Phone, MapPin, StopCircle, Square, LogOut, UsersRound, CreditCard, KeyRound, Globe2, CalendarDays, Plug,
} from "lucide-react";
import {
  getStats, getJobs, registerJob, registerBatch, stopBatch, verifyLookup, deleteJob, clearJobs,
  downloadZip, certUrl, certParts, PART_LABEL, setInstallDate,
} from "@/api";
import StatusBadge from "@/components/StatusBadge";
import UploadModal from "@/components/UploadModal";
import SettingsModal from "@/components/SettingsModal";
import JobConsoleModal from "@/components/JobConsoleModal";
import AssistantChat from "@/components/AssistantChat";
import TeamModal from "@/components/TeamModal";
import BillingModal from "@/components/BillingModal";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import PlatformModal from "@/components/PlatformModal";
import IntegrationsModal from "@/components/IntegrationsModal";
import LegalFooter from "@/components/LegalFooter";

const FILTERS = ["all", "pending", "in_progress", "registered", "needs_review", "failed"];
const FILTER_LABELS = {
  all: "All", pending: "Pending", in_progress: "In Progress", registered: "Registered", needs_review: "Needs Review", failed: "Failed",
};

function KpiCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <div data-testid={testId} className="rounded-xl border border-slate-800/80 bg-[#161f32] p-5 shadow-lg transition-all hover:border-slate-700">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</p>
          <p className="mt-1 font-display text-3xl font-bold text-slate-100">{value}</p>
        </div>
        <div className={`flex h-11 w-11 items-center justify-center rounded-lg ${accent}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

export default function App({ user, onLogout, refreshUser }) {
  const [stats, setStats] = useState(null);
  const [teamOpen, setTeamOpen] = useState(false);
  const [billingOpen, setBillingOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [platformOpen, setPlatformOpen] = useState(false);
  const [integrationsOpen, setIntegrationsOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [dateValue, setDateValue] = useState("");

  const applyInstallDate = async () => {
    if (!dateValue) return toast.error("Pick a date");
    const [y, mo, d] = dateValue.split("-");
    const ids = Array.from(selected);
    try {
      const r = await setInstallDate(ids, `${mo}/${d}/${y}`);
      toast.success(`Install date ${r.install_date} set on ${r.updated} customer${r.updated === 1 ? "" : "s"}`);
      setDateOpen(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not set the install date");
    }
  };
  const isAdmin = user && (user.role === "owner" || user.role === "admin");
  const tenant = user?.tenant;
  const [jobs, setJobs] = useState([]);
  const missingDateCount = jobs.filter((j) => !(j.install_date || "").trim() && j.status !== "registered").length;
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  const [uploadOpen, setUploadOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [consoleJob, setConsoleJob] = useState(null);
  const [settings, setSettings] = useState(null);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [clearText, setClearText] = useState("");

  const refresh = useCallback(async () => {
    try {
      const [s, j] = await Promise.all([getStats(), getJobs()]);
      setStats(s);
      setJobs(j);
    } catch (e) { /* ignore transient */ }
  }, []);

  useEffect(() => {
    refresh();
    import("@/api").then(({ getSettings }) => getSettings().then(setSettings));
  }, [refresh]);

  // poll while any job running
  useEffect(() => {
    const anyRunning = jobs.some((j) => j.status === "in_progress");
    const t = setInterval(refresh, anyRunning ? 2500 : 8000);
    return () => clearInterval(t);
  }, [jobs, refresh]);

  const filtered = useMemo(() => {
    return jobs.filter((j) => {
      if (filter !== "all" && j.status !== filter) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = `${j.first_name} ${j.last_name} ${j.city} ${j.state} ${j.zip} ${(j.serials || []).join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [jobs, filter, search]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };
  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };
  const allVisibleSelected = filtered.length > 0 && filtered.every((j) => selected.has(j.id));
  const toggleAll = () => {
    if (allVisibleSelected) setSelected(new Set());
    else setSelected(new Set(filtered.map((j) => j.id)));
  };

  const ensureVendor = () => {
    if (settings && !settings.is_complete) {
      toast.error("Add your company details in Settings first");
      setSettingsOpen(true);
      return false;
    }
    return true;
  };

  const runOne = async (id) => {
    if (!ensureVendor()) return;
    try {
      await registerJob(id);
      toast.info("Registration started");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start");
      if (e?.response?.status === 402) setBillingOpen(true);
    }
  };

  const runSelected = async () => {
    if (!ensureVendor()) return;
    const ids = [...selected];
    if (!ids.length) return toast.error("Select at least one customer");
    let res;
    try { res = await registerBatch(ids); } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start");
      if (e?.response?.status === 402) setBillingOpen(true);
      return;
    }
    if (res.skipped_registered > 0) {
      toast.info(`Queued ${res.started} • skipped ${res.skipped_registered} already-registered customer(s)`);
    } else {
      toast.info(`Queued ${res.started} registration(s)`);
    }
    setSelected(new Set());
    refresh();
  };

  const retryFailed = async () => {
    if (!ensureVendor()) return;
    const ids = jobs.filter((j) => j.status === "failed" || j.status === "needs_review").map((j) => j.id);
    if (!ids.length) return toast.error("Nothing to retry");
    await registerBatch(ids);
    toast.info(`Retrying ${ids.length} registration(s)`);
    refresh();
  };

  const verifyOnTrane = async () => {
    const ids = selected.size
      ? [...selected]
      : jobs.filter((j) => j.status === "failed" || j.status === "needs_review").map((j) => j.id);
    if (!ids.length) return toast.error("No failed / needs-review customers to verify");
    const res = await verifyLookup(ids);
    if (res.queued > 0) {
      toast.info(`Checking Trane's warranty lookup for ${res.queued} customer(s) — already-registered ones will move to Registered`);
    } else {
      toast.info("Nothing to verify");
    }
    setSelected(new Set());
    refresh();
  };

  const stopAll = async () => {
    try {
      await stopBatch(null);
      toast.warning("Stopping batch — the current customer will finish, then the rest return to Pending");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not stop");
    }
  };

  const stopSelected = async () => {
    const ids = [...selected];
    if (!ids.length) return toast.error("Select at least one customer to stop");
    try {
      await stopBatch(ids);
      toast.warning(`Stopping ${ids.length} selected — any already registering will finish`);
      setSelected(new Set());
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not stop");
    }
  };

  const stopOne = async (id) => {
    try {
      await stopBatch([id]);
      toast.warning("Stopping — if already registering it will finish, otherwise it returns to Pending");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not stop");
    }
  };

  const downloadSelected = async () => {
    const ids = [...selected].filter((id) => {
      const j = jobs.find((x) => x.id === id);
      return j && j.status === "registered";
    });
    try {
      await downloadZip(ids.length ? ids : null);
      toast.success("ZIP download started");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No certificates available");
    }
  };

  const removeJob = async (id) => {
    await deleteJob(id);
    setSelected((p) => { const n = new Set(p); n.delete(id); return n; });
    refresh();
  };

  const clearAll = async () => {
    if (clearText.trim().toUpperCase() !== "CONFIRM") {
      toast.error('Type CONFIRM to clear all customers');
      return;
    }
    await clearJobs();
    setSelected(new Set());
    setClearConfirmOpen(false);
    setClearText("");
    refresh();
    toast.success("All customers cleared");
  };

  const anyRunning = jobs.some((j) => j.status === "in_progress");

  return (
    <div className="App grain min-h-screen bg-[#0a0e17] text-slate-100">
      <Toaster theme="dark" position="top-right" richColors />

      {/* Header */}
      <header className="sticky top-0 z-40 h-16 border-b border-slate-800 bg-[#0f172a]/90 backdrop-blur-md">
        <div className="mx-auto flex h-full max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <img src="/ruach-logo.jpg" alt="R.U.A.C.H." data-testid="app-logo" className="h-10 w-10 rounded-lg object-cover ring-1 ring-orange-500/30" />
            <div>
              <h1 className="font-display text-lg font-bold leading-none tracking-tight">R.U.A.C.H.</h1>
              <p className="text-[11px] text-slate-500">Registration Utility &amp; Automated Certificate Hub</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="open-settings-button"
              onClick={() => setSettingsOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-800/50 px-3 py-2 text-sm font-medium text-slate-300 hover:bg-slate-800"
            >
              <SettingsIcon className="h-4 w-4" /> <span className="hidden sm:inline">Defaults</span>
            </button>
            <button
              data-testid="csv-upload-button"
              onClick={() => setUploadOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500"
            >
              <Upload className="h-4 w-4" /> Import List
            </button>
            {user?.is_platform_admin && (
              <button
                data-testid="open-platform-button"
                onClick={() => setPlatformOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-orange-500/40 bg-orange-500/10 px-3 py-2 text-sm font-medium text-orange-300 hover:bg-orange-500/20"
              >
                <Globe2 className="h-4 w-4" /> <span className="hidden sm:inline">Platform</span>
              </button>
            )}
            {isAdmin && (
              <button
                data-testid="open-team-button"
                onClick={() => setTeamOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-800/50 px-3 py-2 text-sm font-medium text-slate-300 hover:bg-slate-800"
              >
                <UsersRound className="h-4 w-4" /> <span className="hidden sm:inline">Team</span>
              </button>
            )}
            <button
              data-testid="open-integrations-button"
              onClick={() => setIntegrationsOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-800/50 px-3 py-2 text-sm font-medium text-slate-300 hover:bg-slate-800"
            >
              <Plug className="h-4 w-4" /> <span className="hidden sm:inline">Integrations</span>
            </button>
            <button
              data-testid="open-billing-button"
              onClick={() => setBillingOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-800/50 px-3 py-2 text-sm font-medium text-slate-300 hover:bg-slate-800"
            >
              <CreditCard className="h-4 w-4" /> <span className="hidden sm:inline">Billing</span>
              {tenant && <span data-testid="plan-badge" className="ml-1 rounded-md bg-orange-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-300">{tenant.plan_label}</span>}
            </button>
            {user && (
              <div className="flex items-center gap-2 border-l border-slate-700 pl-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-700 text-xs font-semibold text-slate-200">
                  {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="hidden md:block">
                  <span data-testid="current-user-email" className="block max-w-[160px] truncate text-xs text-slate-300">{user.email}</span>
                  <span data-testid="current-tenant-name" className="block max-w-[160px] truncate text-[10px] text-slate-500">{tenant?.name} · {user.role}</span>
                </div>
                <button
                  data-testid="change-password-button"
                  onClick={() => setPwOpen(true)}
                  title="Change password"
                  className="inline-flex items-center rounded-lg border border-slate-700 bg-slate-800/50 px-2.5 py-2 text-sm text-slate-300 hover:bg-slate-800"
                >
                  <KeyRound className="h-4 w-4" />
                </button>
                <button
                  data-testid="logout-button"
                  onClick={onLogout}
                  title="Sign out"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-800/50 px-2.5 py-2 text-sm font-medium text-slate-300 hover:bg-slate-800 hover:text-rose-400"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {/* Vendor / company details banner (editable) */}
        {settings && !settings.is_complete ? (
          <button
            data-testid="setup-company-banner"
            onClick={() => setSettingsOpen(true)}
            className="mb-6 flex w-full items-center gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 px-5 py-4 text-left transition-colors hover:bg-amber-500/15"
          >
            <AlertTriangle className="h-5 w-5 shrink-0 text-amber-400" />
            <div className="flex-1">
              <p className="font-semibold text-amber-200">Set up your company details to start registering</p>
              <p className="text-xs text-amber-400/90">Enter your HVAC/vendor company info, shared contact email &amp; phone, and installer details. Click to configure.</p>
            </div>
            <span className="rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-slate-900">Set Up Now</span>
          </button>
        ) : (
          <div data-testid="company-info-card" className="mb-6 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border border-slate-800/80 bg-gradient-to-r from-[#161f32] to-[#131a2a] px-5 py-3 text-sm">
            <div className="flex items-center gap-2 font-semibold text-slate-200">
              <Building2 className="h-4 w-4 text-orange-500" /> {settings?.company_name || "Your Company"}
            </div>
            {settings?.company_address && <div className="flex items-center gap-1.5 text-slate-400"><MapPin className="h-3.5 w-3.5" /> {settings.company_address}</div>}
            {settings?.shared_email && <div className="flex items-center gap-1.5 text-slate-400"><Mail className="h-3.5 w-3.5" /> {settings.shared_email}</div>}
            {settings?.shared_phone && <div className="flex items-center gap-1.5 text-slate-400"><Phone className="h-3.5 w-3.5" /> {settings.shared_phone}</div>}
            <button data-testid="edit-company-link" onClick={() => setSettingsOpen(true)} className="ml-auto flex items-center gap-1.5 rounded-full border border-slate-700 px-2.5 py-0.5 text-xs text-slate-400 hover:bg-slate-800 hover:text-slate-200">
              <SettingsIcon className="h-3 w-3" /> Edit
            </button>
            <span className="rounded-full border border-orange-500/30 bg-orange-500/10 px-2.5 py-0.5 text-xs font-medium text-orange-400">Homeowner registration</span>
          </div>
        )}

        {/* KPIs */}
        <div className="mb-6 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
          <KpiCard testId="kpi-customers" icon={Users} label="Customers" value={stats?.total_customers ?? 0} accent="bg-blue-500/10 text-blue-400" />
          <KpiCard testId="kpi-equipment" icon={Cpu} label="Equipment Units" value={stats?.total_equipment ?? 0} accent="bg-purple-500/10 text-purple-400" />
          <KpiCard testId="kpi-registered" icon={CheckCircle2} label={`Registered (${stats?.success_rate ?? 0}%)`} value={stats?.registered ?? 0} accent="bg-emerald-500/10 text-emerald-400" />
          <KpiCard testId="kpi-attention" icon={AlertTriangle} label="Needs Review / Failed" value={(stats?.needs_review ?? 0) + (stats?.failed ?? 0)} accent="bg-amber-500/10 text-amber-400" />
        </div>

        {/* Toolbar */}
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            {FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`filter-${f}`}
                onClick={() => setFilter(f)}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  filter === f ? "bg-slate-100 text-slate-900" : "border border-slate-700 bg-slate-800/40 text-slate-300 hover:bg-slate-800"
                }`}
              >
                {FILTER_LABELS[f]}
                {f !== "all" && stats && (
                  <span className="ml-1.5 text-xs opacity-70">{stats[f] ?? 0}</span>
                )}
              </button>
            ))}
          </div>
          <div className="relative w-full lg:w-72">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="customer-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, city, serial..."
              className="w-full rounded-lg border border-slate-700 bg-slate-900/60 py-2 pl-9 pr-3 text-sm text-slate-100 outline-none focus:border-blue-500"
            />
          </div>
        </div>

        {/* Bulk actions bar */}
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-slate-800/80 bg-[#111827] px-4 py-2.5">
          <span className="text-sm text-slate-400">{selected.size} selected</span>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <button data-testid="set-install-date-button" onClick={() => setDateOpen(true)} title="Set the install date on the selected customers (or on every customer still missing one)" className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium ${missingDateCount ? "border-amber-500/60 bg-amber-500/15 text-amber-300 hover:bg-amber-500/25" : "border-slate-700 bg-slate-800/50 text-slate-300 hover:bg-slate-800"}`}>
              <CalendarDays className="h-4 w-4" /> Set Install Date{missingDateCount ? <span data-testid="missing-date-count" className="rounded-md bg-amber-500/30 px-1.5 text-[10px] font-bold">{missingDateCount} missing</span> : null}
            </button>
            <button data-testid="batch-register-trigger-button" onClick={runSelected} className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-500">
              <Zap className="h-4 w-4" /> Register Selected
            </button>
            <button data-testid="stop-selected-button" onClick={stopSelected} className="inline-flex items-center gap-1.5 rounded-lg border border-amber-600/40 bg-amber-600/10 px-3 py-1.5 text-sm font-medium text-amber-400 hover:bg-amber-600/20">
              <Square className="h-3.5 w-3.5" /> Stop Selected
            </button>
            {anyRunning && (
              <button data-testid="stop-all-button" onClick={stopAll} className="inline-flex items-center gap-1.5 rounded-lg border border-rose-600/50 bg-rose-600/15 px-3 py-1.5 text-sm font-semibold text-rose-300 hover:bg-rose-600/25">
                <StopCircle className="h-4 w-4" /> Stop All
              </button>
            )}
            <button data-testid="retry-failed-button" onClick={retryFailed} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-800/50 px-3 py-1.5 text-sm font-medium text-slate-300 hover:bg-slate-800">
              <RefreshCw className="h-4 w-4" /> Retry Failed
            </button>
            <button data-testid="verify-trane-button" onClick={verifyOnTrane} title="Check Trane's lookup and move already-registered customers to Registered (no re-registration)" className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-600/40 bg-indigo-600/10 px-3 py-1.5 text-sm font-medium text-indigo-300 hover:bg-indigo-600/20">
              <Search className="h-4 w-4" /> Verify on Trane
            </button>
            <button data-testid="download-zip-button" onClick={downloadSelected} className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-600/40 bg-emerald-600/10 px-3 py-1.5 text-sm font-medium text-emerald-400 hover:bg-emerald-600/20">
              <Download className="h-4 w-4" /> Download PDFs (ZIP)
            </button>
            {jobs.length > 0 && (
              <button data-testid="clear-all-button" onClick={() => { setClearText(""); setClearConfirmOpen(true); }} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-1.5 text-sm font-medium text-slate-400 hover:bg-slate-800 hover:text-rose-400">
                <Trash2 className="h-4 w-4" /> Clear
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-xl border border-slate-800/80 bg-[#131a2a]">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-800 bg-[#0f172a] text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="w-10 px-4 py-3">
                  <input data-testid="select-all-checkbox" type="checkbox" checked={allVisibleSelected} onChange={toggleAll} className="h-4 w-4 accent-blue-500" />
                </th>
                <th className="w-8 px-2 py-3"></th>
                <th className="px-4 py-3">Job #</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Location</th>
                <th className="px-4 py-3 text-center">Units</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/70">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-16 text-center">
                    <FileText className="mx-auto mb-3 h-10 w-10 text-slate-700" />
                    <p className="text-slate-400">{jobs.length === 0 ? "No customers yet. Import a CSV/Excel list to get started." : "No customers match this filter."}</p>
                  </td>
                </tr>
              )}
              {filtered.map((j) => {
                const isExpanded = expanded.has(j.id);
                return (
                  <React.Fragment key={j.id}>
                    <tr data-testid={`customer-row-${j.id}`} className="transition-colors hover:bg-slate-800/30">
                      <td className="px-4 py-3">
                        <input data-testid={`select-${j.id}`} type="checkbox" checked={selected.has(j.id)} onChange={() => toggleSelect(j.id)} className="h-4 w-4 accent-blue-500" />
                      </td>
                      <td className="px-2 py-3">
                        <button data-testid={`expand-${j.id}`} onClick={() => toggleExpand(j.id)} className="text-slate-500 hover:text-slate-300">
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <span data-testid={`job-number-${j.id}`} className="font-mono text-xs font-semibold text-slate-300">
                          {j.job_number || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => setConsoleJob(j.id)} className="text-left font-medium text-slate-100 hover:text-blue-400">
                          {j.first_name} {j.last_name}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-slate-400">{[j.city, j.state].filter(Boolean).join(", ") || "—"}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-slate-800 px-2 text-xs font-semibold text-slate-300">{j.serials?.length || 0}</span>
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={j.status} /></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          {j.status !== "in_progress" && j.status !== "registered" && (
                            <button data-testid={`register-${j.id}`} onClick={() => runOne(j.id)} title={j.status === "failed" ? "Retry" : "Register"} className="rounded-lg bg-blue-600/90 p-1.5 text-white hover:bg-blue-500">
                              {j.status === "failed" ? <RefreshCw className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                            </button>
                          )}
                          {j.status === "in_progress" && (
                            <button data-testid={`stop-${j.id}`} onClick={() => stopOne(j.id)} title="Stop this registration" className="rounded-lg bg-amber-600/90 p-1.5 text-white hover:bg-amber-500">
                              <Square className="h-4 w-4" />
                            </button>
                          )}
                          {(j.status === "registered" || j.status === "needs_review") && certParts(j).map((part) => (
                            <a key={part} data-testid={`download-pdf-button-${j.id}-${part}`} href={certUrl(j.id, part)} title={`Download ${PART_LABEL[part] || part} PDF`} className="inline-flex items-center gap-1 rounded-lg bg-emerald-600/90 p-1.5 text-xs font-semibold text-white hover:bg-emerald-500">
                              <Download className="h-4 w-4" />{certParts(j).length > 1 && (PART_LABEL[part] || part)[0]}
                            </a>
                          ))}
                          <button data-testid={`view-console-${j.id}`} onClick={() => setConsoleJob(j.id)} title="View console" className="rounded-lg border border-slate-700 p-1.5 text-slate-400 hover:bg-slate-800">
                            <FileText className="h-4 w-4" />
                          </button>
                          <button data-testid={`delete-${j.id}`} onClick={() => removeJob(j.id)} title="Remove" className="rounded-lg border border-slate-700 p-1.5 text-slate-500 hover:bg-slate-800 hover:text-rose-400">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-[#0f172a]/60">
                        <td colSpan={8} className="px-6 py-4">
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                            <div className="md:col-span-2">
                              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Equipment Serial Numbers</p>
                              <div className="flex flex-wrap gap-2">
                                {(j.serials || []).length === 0 && <span className="text-sm text-slate-500">No serials on file</span>}
                                {(j.serials || []).map((s, i) => {
                                  const res = (j.serial_results || []).find((r) => r.serial === s);
                                  const ok = res && res.status === "added";
                                  const bad = res && res.status !== "added";
                                  return (
                                    <span key={i} data-testid={`equipment-serial-${i}`} title={res ? res.status : ""} className={`inline-flex items-center gap-1 rounded border px-2 py-1 font-mono text-xs font-semibold tracking-wider ${ok ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : bad ? "border-rose-500/40 bg-rose-500/10 text-rose-400 line-through" : "border-slate-700/60 bg-slate-900/80 text-slate-300"}`}>
                                      {ok && <CheckCircle2 className="h-3 w-3" />}
                                      {bad && <AlertTriangle className="h-3 w-3" />}
                                      {s}
                                    </span>
                                  );
                                })}
                              </div>
                              {(j.failed_serials || []).length > 0 && (
                                <p className="mt-2 text-xs text-rose-400">{j.failed_serials.length} serial(s) skipped — see status on hover.</p>
                              )}
                            </div>
                            <div>
                              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Details</p>
                              <p className="text-sm text-slate-300">Job #: <span className="font-mono text-slate-200">{j.job_number || "—"}</span></p>
                              <p className="text-sm text-slate-300">{j.address1} {j.address2}</p>
                              <p className="text-sm text-slate-400">{j.city}, {j.state} {j.zip}</p>
                              <p className="mt-1 text-sm text-slate-400">Installed: {j.install_date || <span data-testid={`no-date-${j.id}`} className="rounded-md bg-amber-500/20 px-1.5 py-0.5 text-[11px] font-semibold text-amber-300">No install date</span>}</p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </main>
      <LegalFooter />

      <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} onUploaded={refresh} />
      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} onSaved={(s) => { setSettings(s); refresh(); }} />
      <TeamModal open={teamOpen} onClose={() => setTeamOpen(false)} me={user} />
      <BillingModal open={billingOpen} onClose={() => { setBillingOpen(false); refreshUser && refreshUser(); }} me={user} onDataDeleted={refresh} />
      <PlatformModal open={platformOpen} onClose={() => setPlatformOpen(false)} />
      <IntegrationsModal open={integrationsOpen} onClose={() => setIntegrationsOpen(false)} me={user} />
      {dateOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="install-date-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setDateOpen(false)} />
          <div className="relative z-10 w-full max-w-md rounded-2xl border border-slate-700 bg-[#161f32] p-6 shadow-2xl fade-up">
            <h2 className="flex items-center gap-2 font-display text-xl font-semibold text-slate-100"><CalendarDays className="h-5 w-5 text-amber-400" /> Set Install Date</h2>
            <p className="mt-2 text-sm text-slate-400">
              {selected.size > 0
                ? `Applies to the ${selected.size} selected customer${selected.size === 1 ? "" : "s"} (registered customers are left unchanged).`
                : `No customers selected — applies to all ${missingDateCount} customer${missingDateCount === 1 ? "" : "s"} currently missing an install date.`}
            </p>
            <input data-testid="install-date-input" type="date" value={dateValue} onChange={(e) => setDateValue(e.target.value)} className="mt-4 w-full rounded-lg border border-slate-700 bg-slate-900/60 px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-amber-500" />
            <div className="mt-5 flex justify-end gap-2">
              <button data-testid="install-date-cancel" onClick={() => setDateOpen(false)} className="rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800">Cancel</button>
              <button data-testid="install-date-apply" onClick={applyInstallDate} className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-500">Apply date</button>
            </div>
          </div>
        </div>
      )}
      <ChangePasswordModal open={pwOpen || !!user?.must_change_password} forced={!!user?.must_change_password} onClose={() => setPwOpen(false)} onChanged={refreshUser} />
      <JobConsoleModal jobId={consoleJob} open={!!consoleJob} onClose={() => setConsoleJob(null)} onChanged={refresh} />

      <AssistantChat />

      {clearConfirmOpen && (
        <div data-testid="clear-confirm-overlay" className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-rose-600/40 bg-[#131a2a] p-6 shadow-2xl">
            <div className="mb-3 flex items-center gap-2 text-rose-400">
              <AlertTriangle className="h-5 w-5" />
              <h3 className="font-display text-lg font-bold">Clear all customers?</h3>
            </div>
            <p className="mb-4 text-sm text-slate-400">
              This permanently removes <span className="font-semibold text-slate-200">{jobs.length}</span> customer{jobs.length === 1 ? "" : "s"} and their progress from the queue. This cannot be undone.
              Type <span className="font-mono font-semibold text-rose-300">CONFIRM</span> to proceed.
            </p>
            <input
              data-testid="clear-confirm-input"
              autoFocus
              value={clearText}
              onChange={(e) => setClearText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") clearAll(); }}
              placeholder="Type CONFIRM"
              className="mb-4 w-full rounded-lg border border-slate-700 bg-slate-900/60 px-3 py-2 text-sm text-slate-100 outline-none focus:border-rose-500"
            />
            <div className="flex justify-end gap-2">
              <button data-testid="clear-cancel-button" onClick={() => { setClearConfirmOpen(false); setClearText(""); }} className="rounded-lg border border-slate-700 px-4 py-2 text-sm font-medium text-slate-300 hover:bg-slate-800">
                Cancel
              </button>
              <button
                data-testid="clear-confirm-button"
                onClick={clearAll}
                disabled={clearText.trim().toUpperCase() !== "CONFIRM"}
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-500 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
